/**
 * Permission Engine - Resource Evaluators
 *
 * Specific permission evaluation logic for different resource types
 */

import {
  UserContext,
  PermissionRequirement,
  PermissionResult,
  ProjectMembership,
  CompanyContract,
  PermissionAction,
  PermissionResource
} from '../types';
import { createSuccessResult, createErrorResult, isSystemAdmin } from './validation';
import { evaluateCompanyPermission } from './company-permissions';
import { evaluateProjectPermission } from './project-permissions';

/**
 * Main resource permission evaluation dispatcher
 */
export async function evaluateResourcePermission(
  user: UserContext,
  requirement: PermissionRequirement,
  projectMemberships: ProjectMembership[],
  companyContracts: CompanyContract[]
): Promise<PermissionResult> {
  // System admins have access to everything
  if (isSystemAdmin(user)) {
    return createSuccessResult('SYSTEM_ADMIN', 'System admin access granted');
  }

  // Route to specific resource evaluators
  switch (requirement.resource) {
    case PermissionResource.COMPANY:
      return evaluateCompanyPermission(user, requirement, companyContracts);

    case PermissionResource.PROJECT:
    case PermissionResource.SUBPROJECT:
    case PermissionResource.REPORT:
      return evaluateProjectPermission(user, requirement, projectMemberships);

    case PermissionResource.USER:
      return evaluateUserPermission(user, requirement);

    case PermissionResource.TRUCK:
    case PermissionResource.ITEM:
      return evaluateOperationalResourcePermission(user, requirement);

    case PermissionResource.NEWSLETTER:
      return evaluateNewsletterPermission(user, requirement);

    default:
      return createErrorResult('UNSUPPORTED_RESOURCE', `Resource ${requirement.resource} is not supported`);
  }
}

/**
 * Evaluate user resource permissions
 */
function evaluateUserPermission(user: UserContext, requirement: PermissionRequirement): PermissionResult {
  const { action, resourceId, companyScope } = requirement;

  // Users can always view their own profile
  if (action === PermissionAction.VIEW && resourceId === user.id) {
    return createSuccessResult('SELF_ACCESS', 'User can view own profile');
  }

  // Company Admins can manage users in their company
  if (user.app_role === 'COMPANY_ADMIN' && user.company_id) {
    if (companyScope && companyScope !== user.company_id) {
      return createErrorResult('COMPANY_SCOPE_MISMATCH', 'Cannot access users outside company');
    }

    switch (action) {
      case PermissionAction.VIEW:
      case PermissionAction.EDIT:
      case PermissionAction.CREATE:
        return createSuccessResult('COMPANY_ADMIN_USER_ACCESS', 'Company admin can manage company users');
      case PermissionAction.DELETE:
        if (resourceId === user.id) {
          return createErrorResult('SELF_DELETION', 'Cannot delete own account');
        }
        return createSuccessResult('COMPANY_ADMIN_DELETE', 'Company admin can delete company users');
      default:
        return createErrorResult('UNSUPPORTED_ACTION', `Action ${action} not supported for user resource`);
    }
  }

  // Regular users can view users (database RPC enforces row-level filtering)
  if (action === PermissionAction.VIEW) {
    // Allow VIEW action for all authenticated users
    // Security note: Database RPC function get_users_paginated enforces:
    //   - Regular users see only themselves
    //   - Company Admins see all company users
    //   - System Admins see all users
    return createSuccessResult('USER_VIEW', 'User can view user list');
  }

  return createErrorResult('INSUFFICIENT_PERMISSIONS', 'Insufficient permissions for user resource');
}

/**
 * Evaluate operational resource permissions (trucks, reports, items)
 */
function evaluateOperationalResourcePermission(
  user: UserContext,
  requirement: PermissionRequirement
): PermissionResult {
  const { action, resource, companyScope } = requirement;

  // Company scope validation
  if (companyScope && user.company_id !== companyScope) {
    return createErrorResult('COMPANY_SCOPE_MISMATCH', 'Cannot access resources outside company');
  }

  // CLIENT company restriction: Cannot create, edit, or delete trucks
  if (resource === PermissionResource.TRUCK &&
      (action === PermissionAction.CREATE || action === PermissionAction.EDIT || action === PermissionAction.DELETE) &&
      user.company_type === 'CLIENT') {
    return createErrorResult('CLIENT_COMPANY_RESTRICTION', 'CLIENT companies cannot create, edit, or delete trucks');
  }

  // CLIENT company restriction: Cannot create reports
  if (resource === PermissionResource.REPORT &&
      action === PermissionAction.CREATE &&
      user.company_type === 'CLIENT') {
    return createErrorResult('CLIENT_COMPANY_RESTRICTION', 'CLIENT companies cannot create reports');
  }

  // Company Admins and Users with proper company context can perform most operations
  if (user.company_id && (user.app_role === 'COMPANY_ADMIN' || user.app_role === 'USER')) {
    switch (action) {
      case PermissionAction.VIEW:
      case PermissionAction.CREATE:
      case PermissionAction.EDIT:
        return createSuccessResult('COMPANY_OPERATIONAL_ACCESS', 'Company member can perform operational actions');
      case PermissionAction.DELETE:
        if (user.app_role === 'COMPANY_ADMIN') {
          return createSuccessResult('COMPANY_ADMIN_DELETE', 'Company admin can delete operational resources');
        }
        return createErrorResult('DELETE_RESTRICTED', 'Delete requires Company Admin role');
      default:
        return createErrorResult('UNSUPPORTED_ACTION', `Action ${action} not supported for ${requirement.resource}`);
    }
  }

  return createErrorResult('INSUFFICIENT_PERMISSIONS', `Insufficient permissions for ${requirement.resource}`);
}

/**
 * Evaluate newsletter resource permissions
 * Newsletter features are restricted to System Admins only
 */
function evaluateNewsletterPermission(
  user: UserContext,
  requirement: PermissionRequirement
): PermissionResult {
  const { action } = requirement;

  // Newsletter features are System Admin only
  if (isSystemAdmin(user)) {
    return createSuccessResult('SYSTEM_ADMIN_NEWSLETTER', 'System admin can access newsletter features');
  }

  // All other users are denied
  return createErrorResult(
    'NEWSLETTER_SYSTEM_ADMIN_ONLY',
    `Newsletter ${action} requires System Admin privileges`
  );
}